import type { PlaySession, PlaySessionStartRequest } from "@slot/client-engine";
import type { AssetRegistry } from "../assets/AssetRegistry";
import type { GameBootDefinition } from "../composition/GameDefinition";
import { CurrencyCatalogLoader } from "../money/CurrencyCatalogLoader";
import type { CurrencyCatalog } from "../money/CurrencyCatalogTypes";
import type { BootConfig } from "./BootConfig";
import type { BootProgress } from "./BootProgress";
import { BACKEND_API_PATHS } from "./defaults/BackendApiPaths";
import { SHELL_BUNDLE_NAMES } from "./defaults/BundleNames";
import { LaunchContextLoader } from "./launch/LaunchContextLoader";
import type { LaunchContext } from "./launch/LaunchContextTypes";
import type { GameLink } from "./launch/readGameLink";
import { retryWithBackoff } from "./retry/RetryWithBackoff";
import { PlaySessionLoader } from "./session/PlaySessionLoader";
import { GameInitialSettingsLoader } from "./settings/GameInitialSettingsLoader";
import type { GameInitialSettings } from "./settings/GameInitialSettingsTypes";
import { composeBackendUrl } from "./utils/backendUrl";
import { type LaunchContextSourceMode, resolveLaunchContextSource } from "./utils/launchContextSource";
import { readQueryParam } from "./utils/readQueryParam";
import type { Log } from "@slot/presentation";

/** What every boot load shares: the backend (empty = the embedded fixtures), the config, the page's query, the loader, the log. */
export interface BootLoadContext {
  backendBaseUrl: string;
  config: BootConfig;
  queryString: string | null;
  progress: BootProgress;
  log: Log;
}

const LAUNCH_CLIENT_TYPES = new Set(["mobile", "tablet", "desktop", "unknown"]);

/** A backend path, or "fixture" when no backend is configured. */
function backendUrlOrFixture(ctx: BootLoadContext, path: string): string {
  return ctx.backendBaseUrl === "" ? "fixture" : composeBackendUrl(ctx.backendBaseUrl, path);
}

/**
 * The play session (decisions 80, 81), started on a normal boot against a backend only. Never offline, where there is
 * no backend to ask, and never for `?replay=`: starting a session ends the player's live one, and a replay link must not
 * end the tab the player is playing in. Its body is the link's launch, else its free-play currency, else empty. Null
 * offline, in a replay, and on a backend without sessions (404); a refused launch rejects with `LaunchRefusedError`.
 */
export async function startPlaySession(ctx: BootLoadContext, input: { replay: boolean; link: GameLink }): Promise<PlaySession | null> {
  if (ctx.backendBaseUrl === "" || input.replay) return null;
  const { launch, currency } = input.link;
  const request: PlaySessionStartRequest = launch !== null ? { launch } : currency !== null ? { currency } : {};
  const session = await retryWithBackoff({
    taskName: "play_session",
    log: ctx.log,
    run: () => new PlaySessionLoader().start(composeBackendUrl(ctx.backendBaseUrl, BACKEND_API_PATHS.playSession), request),
    onRetrying: (state) => ctx.progress.retrying("session", state),
  });
  ctx.log(session ? `play session started (${launch !== null ? "the link's launch" : currency !== null ? `free play in ${currency}` : "no launch"})` : "no play sessions on this backend (404): no bearer, requests go unnumbered");
  return session;
}

/**
 * The operator settings, the player's when the boot started a session (its bearer, decision 81); in dev
 * `?pending=<roundId>` stands in for a backend-reported interrupted round.
 */
export async function loadInitialSettings(ctx: BootLoadContext, game: GameBootDefinition, playSession: PlaySession | null): Promise<GameInitialSettings> {
  const loader = new GameInitialSettingsLoader({ fixtureOverrides: game.fixtureSettingsOverrides, playSession });
  const settings = await retryWithBackoff({
    taskName: "initial_settings",
    log: ctx.log,
    run: () => loader.loadFromUrl(backendUrlOrFixture(ctx, BACKEND_API_PATHS.initialSettings)),
    onRetrying: (state) => ctx.progress.retrying("settings", state),
  });
  ctx.log(`initial settings loaded currency=${settings.accountBalanceSnapshot.currencyCode} betSteps=${settings.betStepsMinor.length} initialBet=${settings.initialBetMinor}`);
  const pendingRoundId = ctx.config.devModeEnabled ? readQueryParam(ctx.queryString, "pending") : null;
  if (!pendingRoundId) return settings;
  ctx.log(`pending round override — roundId="${pendingRoundId}"`);
  return { ...settings, pendingRoundId };
}

/** The launch context (device, channel, operator); `?device=` overrides the client type. */
export async function loadLaunchContext(ctx: BootLoadContext, sourceMode: LaunchContextSourceMode): Promise<LaunchContext> {
  const rawDevice = readQueryParam(ctx.queryString, "device");
  const deviceOverride = rawDevice !== null && LAUNCH_CLIENT_TYPES.has(rawDevice) ? rawDevice : null;
  if (rawDevice !== null && deviceOverride === null) ctx.log(`[LaunchContext] ignored ?device="${rawDevice}"`);
  const source = resolveLaunchContextSource({
    sourceMode,
    launchContextFixture: ctx.config.launchContextFixture,
    launchContextBaseUrl: composeBackendUrl(ctx.backendBaseUrl, BACKEND_API_PATHS.launchContext),
    deviceOverride,
  });
  if (deviceOverride !== null) ctx.log(`[LaunchContext] applying ?device="${deviceOverride}" — source=${source}`);
  const launchContext = await retryWithBackoff({
    taskName: "launch_context",
    log: ctx.log,
    run: () => new LaunchContextLoader().load(source, { fixtureOperatorIdMode: ctx.config.fixtureOperatorIdMode }),
    onRetrying: (state) => ctx.progress.retrying("launch_context", state),
  });
  ctx.log(`[LaunchContext] clientType=${launchContext.clientType} channel=${launchContext.channel ?? "unset"} source=${launchContext.source ?? "unknown"}`);
  return launchContext;
}

export function loadCurrencyCatalog(ctx: BootLoadContext): Promise<CurrencyCatalog> {
  return retryWithBackoff({
    taskName: "currency_catalog",
    log: ctx.log,
    run: () => new CurrencyCatalogLoader().loadFromUrl(backendUrlOrFixture(ctx, BACKEND_API_PATHS.currencyCatalog)),
    onRetrying: (state) => ctx.progress.retrying("currency", state),
  });
}

/** The core bundles in parallel, each retried on its own; the shared visuals drive the bar while they stream. */
export async function loadCoreBundles(ctx: BootLoadContext, assets: AssetRegistry, names: readonly string[]): Promise<void> {
  ctx.progress.stage("bundles", "bundles: starting");
  let completed = 0;
  await Promise.all(
    names.map((name) =>
      retryWithBackoff({
        taskName: `bundle_${name}`,
        log: ctx.log,
        run: async () => {
          await assets.loadBundle(name, (p) => {
            if (name === SHELL_BUNDLE_NAMES.gameSharedVisuals) ctx.progress.bundles(p * 0.9);
          });
          completed += 1;
          ctx.progress.bundles(completed / names.length, `bundles: ${completed}/${names.length} loaded — last=${name}`);
        },
        onRetrying: (state) => ctx.progress.retrying(`bundle ${name}`, state),
      }),
    ),
  );
}
